/**
 * Offline Sync Service - Phase 4d: Offline-First Architecture
 *
 * Handles:
 * - Queuing exam answers during offline mode
 * - Background sync when connection restored
 * - Sync status tracking
 * - Conflict resolution for offline submissions
 */

import React from 'react';
import { logger } from '../utils/logger';
import { advancedCache } from './advancedCache';

export interface OfflineAnswer {
  questionId: string;
  selectedOption: string | null;
  timeTaken: number;
  timestamp: number;
  syncedAt?: number;
}

export interface OfflineExamSession {
  examId: string;
  startedAt: number;
  answers: OfflineAnswer[];
  status: 'in-progress' | 'pending-sync' | 'synced' | 'failed';
  lastSyncAttempt?: number;
  syncError?: string;
}

class OfflineSyncService {
  private pendingAnswers = new Map<string, OfflineAnswer[]>();
  private syncInProgress = false;
  private syncListeners = new Set<(status: boolean) => void>();

  constructor() {
    this.setupSyncHandlers();
    this.restoreOfflineData();
  }

  /**
   * Queue an answer for offline submission
   */
  queueAnswer(examId: string, answer: OfflineAnswer): void {
    if (!this.pendingAnswers.has(examId)) {
      this.pendingAnswers.set(examId, []);
    }

    const answers = this.pendingAnswers.get(examId)!;

    // Update or append answer
    const existingIndex = answers.findIndex(a => a.questionId === answer.questionId);
    if (existingIndex >= 0) {
      answers[existingIndex] = answer;
    } else {
      answers.push(answer);
    }

    // Persist to IndexedDB
    this.persistOfflineAnswers(examId).catch(err => {
      logger.error('Error persisting offline answers:', err);
    });

    logger.debug(`Queued answer for ${examId}:`, answer);
  }

  /**
   * Persist offline answers to IndexedDB
   */
  private async persistOfflineAnswers(examId: string): Promise<void> {
    const answers = this.pendingAnswers.get(examId) || [];
    const key = `offline_answers_${examId}`;

    const session: OfflineExamSession = {
      examId,
      startedAt: Date.now(),
      answers,
      status: 'pending-sync'
    };

    await advancedCache.set(key, session, 'default');
  }

  /**
   * Get queued answers for an exam
   */
  getPendingAnswers(examId: string): OfflineAnswer[] {
    return this.pendingAnswers.get(examId) || [];
  }

  /**
   * Get all pending exams waiting for sync
   */
  getPendingExams(): string[] {
    return Array.from(this.pendingAnswers.keys());
  }

  /**
   * Restore offline data from IndexedDB on app load
   */
  private async restoreOfflineData(): Promise<void> {
    try {
      // In a real implementation, you would iterate through all stored offline data
      // For now, this is a placeholder for the logic
      logger.debug('Restoring offline data from IndexedDB...');
    } catch (error) {
      logger.error('Error restoring offline data:', error);
    }
  }

  /**
   * Setup background sync handlers
   */
  private setupSyncHandlers(): void {
    // Listen for online event
    window.addEventListener('online', () => {
      logger.info('Connection restored - starting sync');
      this.syncPendingAnswers().catch(err => {
        logger.error('Error during sync:', err);
      });
    });

    // Register for Background Sync API (if available)
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      navigator.serviceWorker.ready.then(_registration => {
        logger.debug('Background Sync ready');
      }).catch(err => {
        logger.debug('Background Sync not available:', err);
      });
    }
  }

  /**
   * Sync pending answers to server
   */
  async syncPendingAnswers(): Promise<boolean> {
    if (this.syncInProgress) {
      logger.debug('Sync already in progress');
      return false;
    }

    if (this.pendingAnswers.size === 0) {
      logger.debug('No pending answers to sync');
      return true;
    }

    this.syncInProgress = true;
    this.notifySyncListeners(true);

    try {
      let successCount = 0;
      let failureCount = 0;

      const examEntries = Array.from(this.pendingAnswers.entries());
      for (const [examId, answers] of examEntries) {
        try {
          const success = await this.syncExamAnswers(examId, answers);
          if (success) {
            successCount++;
            this.pendingAnswers.delete(examId);
            // Clear from IndexedDB
            await advancedCache.set(`offline_answers_${examId}`, null, 'default');
          } else {
            failureCount++;
          }
        } catch (error) {
          logger.error(`Error syncing exam ${examId}:`, error);
          failureCount++;
        }
      }

      logger.info(`Sync completed: ${successCount} successful, ${failureCount} failed`);
      return failureCount === 0;
    } finally {
      this.syncInProgress = false;
      this.notifySyncListeners(false);
    }
  }

  /**
   * Sync answers for a single exam to API
   */
  private async syncExamAnswers(examId: string, answers: OfflineAnswer[]): Promise<boolean> {
    try {
      // This would call your backend API to submit the answers
      // For now, this is a placeholder
      logger.debug(`Syncing ${answers.length} answers for exam ${examId}`);

      // Simulate API call
      // const response = await fetch('/api/exams/submit', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ examId, answers })
      // });

      // if (!response.ok) {
      //   throw new Error(`Sync failed: ${response.statusText}`);
      // }

      // Mark answers as synced
      answers.forEach(answer => {
        answer.syncedAt = Date.now();
      });

      return true;
    } catch (error) {
      logger.error('Error syncing exam answers:', error);
      return false;
    }
  }

  /**
   * Clear all offline data (after successful sync)
   */
  clearOfflineData(examId?: string): void {
    if (examId) {
      this.pendingAnswers.delete(examId);
    } else {
      this.pendingAnswers.clear();
    }
    logger.debug('Cleared offline data');
  }

  /**
   * Get sync status
   */
  isSyncing(): boolean {
    return this.syncInProgress;
  }

  /**
   * Subscribe to sync status changes
   */
  subscribeSyncStatus(listener: (isSyncing: boolean) => void): () => void {
    this.syncListeners.add(listener);
    return () => {
      this.syncListeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of sync status change
   */
  private notifySyncListeners(isSyncing: boolean): void {
    const listenerArray = Array.from(this.syncListeners);
    for (const listener of listenerArray) {
      listener(isSyncing);
    }
  }

  /**
   * Get storage quota usage (for quota management)
   */
  async getStorageQuotaUsage(): Promise<{
    usage: number;
    quota: number;
    percentUsed: number;
  }> {
    if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
      return { usage: 0, quota: 0, percentUsed: 0 };
    }

    try {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;

      logger.debug(`Storage quota: ${(usage / 1024 / 1024).toFixed(1)}MB / ${(quota / 1024 / 1024).toFixed(1)}MB`);

      return { usage, quota, percentUsed };
    } catch (error) {
      logger.error('Error getting storage quota:', error);
      return { usage: 0, quota: 0, percentUsed: 0 };
    }
  }

  /**
   * Request persistent storage (if user grants permission)
   */
  async requestPersistentStorage(): Promise<boolean> {
    if (!('storage' in navigator) || !('persist' in navigator.storage)) {
      return false;
    }

    try {
      const isPersistent = await navigator.storage.persist();
      if (isPersistent) {
        logger.info('Persistent storage granted');
      }
      return isPersistent;
    } catch (error) {
      logger.error('Error requesting persistent storage:', error);
      return false;
    }
  }
}

// Export singleton instance
export const offlineSync = new OfflineSyncService();

/**
 * React hook for offline sync status
 */
export function useOfflineSync() {
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [pendingExams, setPendingExams] = React.useState<string[]>([]);

  React.useEffect(() => {
    const unsubscribe = offlineSync.subscribeSyncStatus((syncing) => {
      setIsSyncing(syncing);
    });

    // Update pending exams count
    setPendingExams(offlineSync.getPendingExams());

    return unsubscribe;
  }, []);

  return {
    isSyncing,
    pendingExamsCount: pendingExams.length,
    syncNow: () => offlineSync.syncPendingAnswers()
  };
}
